import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateNodesFromDOM } from '@lexical/html';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
    $createParagraphNode,
    $createRangeSelection,
    $getSelection,
    $insertNodes,
    $isRangeSelection,
    $isRootOrShadowRoot,
    $setSelection,
    COMMAND_PRIORITY_EDITOR,
    COMMAND_PRIORITY_HIGH,
    DRAGOVER_COMMAND,
    DROP_COMMAND,
    PASTE_COMMAND,
    createCommand,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import styled from '@emotion/styled';

import { $createImageNode, ImageNode } from '../nodes/ImageNode';
import { uploadInlineImage } from '../redux/ducks/attachment';
import { showAlert } from '../redux/slices/alerts';
import { formatBytes } from '../utils/navigateUtils';

export const INSERT_IMAGE_COMMAND = createCommand('INSERT_IMAGE_COMMAND');

export const MAX_INLINE_IMAGE_BYTES = 20 * 1024 * 1024;

export function inlineImageError(file) {
    if (!file) return 'That file could not be read.';
    const label = file.name ? `"${file.name}"` : 'That file';
    if (!file.type || !file.type.startsWith('image/')) {
        return `${label} is not an image. Only images can be added to the editor — attach other files below the editor instead.`;
    }
    if (file.size >= MAX_INLINE_IMAGE_BYTES) {
        return `${label} is ${formatBytes(file.size)}. Images added to the editor must be under 20 MB.`;
    }
    return null;
}

const UploadStatus = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
}));

function collectFiles(dataTransfer) {
    if (!dataTransfer) return [];
    const fromFiles = dataTransfer.files ? Array.from(dataTransfer.files) : [];
    if (fromFiles.length > 0) return fromFiles;
    const items = dataTransfer.items ? Array.from(dataTransfer.items) : [];
    return items
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter(Boolean);
}

function hasFiles(dataTransfer) {
    if (!dataTransfer) return false;
    const types = dataTransfer.types ? Array.from(dataTransfer.types) : [];
    return types.includes('Files');
}

export function dataUriToFile(dataUri, name) {
    try {
        if (typeof dataUri !== 'string' || !/^data:/i.test(dataUri)) return null;
        const comma = dataUri.indexOf(',');
        if (comma < 0) return null;

        const header = dataUri.slice(5, comma);
        const isBase64 = /;base64$/i.test(header);
        const mimeType = (isBase64 ? header.slice(0, -7) : header).split(';')[0].trim().toLowerCase() || 'text/plain';
        const extension = (mimeType.split('/')[1] || 'png').split('+')[0];
        const payload = dataUri.slice(comma + 1);

        let bytes;
        if (isBase64) {
            const binary = atob(payload);
            bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        } else {
            bytes = new TextEncoder().encode(decodeURIComponent(payload));
        }

        return new File([bytes], `${name}.${extension}`, { type: mimeType });
    } catch {
        return null;
    }
}

function parseClipboardHtml(htmlString) {
    if (!htmlString) return null;
    try {
        return new DOMParser().parseFromString(htmlString, 'text/html');
    } catch {
        return null;
    }
}

function htmlHasContentBeyondImages(dom) {
    const body = dom?.body;
    if (!body) return false;
    if ((body.textContent || '').trim().length > 0) return true;
    return Boolean(body.querySelector('table, ul, ol, hr, video, iframe'));
}

function domRangeFromPoint(event) {
    const doc = event.target?.ownerDocument || document;
    if (doc.caretRangeFromPoint) return doc.caretRangeFromPoint(event.clientX, event.clientY);
    if (doc.caretPositionFromPoint) {
        const position = doc.caretPositionFromPoint(event.clientX, event.clientY);
        if (!position) return null;
        const range = doc.createRange();
        range.setStart(position.offsetNode, position.offset);
        range.collapse(true);
        return range;
    }
    return null;
}

function $captureInsertionPoint() {
    const selection = $getSelection();
    return $isRangeSelection(selection) ? selection.clone() : null;
}

function $insertionPointFromDrop(event, rootElement) {
    const range = domRangeFromPoint(event);
    if (!range || !rootElement || !rootElement.contains(range.startContainer)) return null;
    try {
        const selection = $createRangeSelection();
        selection.applyDOMRange(range);
        if (selection.anchor.key === 'root') return null;
        return selection.clone();
    } catch {
        return null;
    }
}

function $applyCapturedPoint(target, source) {
    const node = source.getNode();
    if (!node || !node.isAttached()) return false;
    const limit = source.type === 'text' ? node.getTextContentSize() : node.getChildrenSize();
    target.set(node.getKey(), Math.min(source.offset, limit), source.type);
    return true;
}

function $restoreInsertionPoint(point) {
    if (!point) return;
    try {
        const selection = $createRangeSelection();
        if (!$applyCapturedPoint(selection.anchor, point.anchor)) return;
        if (!$applyCapturedPoint(selection.focus, point.focus)) return;
        $setSelection(selection);
    } catch {
        return;
    }
}

function $insertImageAtSelection(src) {
    const imageNode = $createImageNode(src);
    $insertNodes([imageNode]);
    if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
        $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
    }
}

export default function ImagesPlugin() {
    const [editor] = useLexicalComposerContext();
    const dispatch = useDispatch();
    const isMountedRef = useRef(true);
    const [pendingUploads, setPendingUploads] = useState(0);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const uploadImage = useCallback(
        (file) => {
            const problem = inlineImageError(file);
            if (problem) {
                dispatch(showAlert({ severity: 'error', message: problem }));
                return Promise.resolve({ url: null, uploadFailed: false });
            }
            if (isMountedRef.current) setPendingUploads((count) => count + 1);
            return new Promise((resolve) => {
                dispatch(
                    uploadInlineImage({
                        file,
                        callback: (data) => {
                            if (isMountedRef.current) setPendingUploads((count) => Math.max(0, count - 1));
                            resolve({ url: data?.url || null, uploadFailed: !data?.url });
                        },
                    }),
                );
            });
        },
        [dispatch],
    );

    const insertUploadedImage = useCallback(
        (url, insertionPoint) => {
            if (!isMountedRef.current) return insertionPoint;
            let nextPoint = insertionPoint;
            editor.update(() => {
                $restoreInsertionPoint(insertionPoint);
                $insertImageAtSelection(url);
                nextPoint = $captureInsertionPoint();
            });
            return nextPoint;
        },
        [editor],
    );

    const insertImageFiles = useCallback(
        async (files, insertionPoint) => {
            let point = insertionPoint;
            for (const file of files) {
                if (!isMountedRef.current) return;
                const { url, uploadFailed } = await uploadImage(file);
                if (url) point = insertUploadedImage(url, point);
                else if (uploadFailed) return;
            }
        },
        [insertUploadedImage, uploadImage],
    );

    const insertPastedHtml = useCallback(
        async (dom, insertionPoint) => {
            const embedded = Array.from(dom.querySelectorAll('img')).filter((img) => /^data:/i.test(img.getAttribute('src') || ''));

            for (let index = 0; index < embedded.length; index += 1) {
                const img = embedded[index];
                const file = dataUriToFile(img.getAttribute('src'), `pasted-image-${index + 1}`);
                const result = file ? await uploadImage(file) : null;
                if (result?.url) {
                    img.setAttribute('src', result.url);
                    continue;
                }
                img.remove();
                if (result?.uploadFailed) {
                    embedded.slice(index + 1).forEach((remaining) => remaining.remove());
                    break;
                }
            }

            if (!isMountedRef.current) return;

            editor.update(
                () => {
                    const nodes = $generateNodesFromDOM(editor, dom);
                    if (nodes.length > 0) {
                        $restoreInsertionPoint(insertionPoint);
                        $insertNodes(nodes);
                    }
                },
                { tag: 'paste' },
            );
        },
        [editor, uploadImage],
    );

    useEffect(() => {
        if (!editor.hasNodes([ImageNode])) {
            throw new Error('ImagesPlugin: ImageNode not registered on editor');
        }

        return mergeRegister(
            editor.registerCommand(
                INSERT_IMAGE_COMMAND,
                (src) => {
                    $insertImageAtSelection(src);
                    return true;
                },
                COMMAND_PRIORITY_EDITOR,
            ),
            editor.registerCommand(
                PASTE_COMMAND,
                (event) => {
                    const clipboardData = event instanceof ClipboardEvent ? event.clipboardData : null;
                    if (!clipboardData) return false;

                    const htmlString = clipboardData.getData('text/html') || '';
                    const imageFiles = collectFiles(clipboardData).filter((file) => file.type && file.type.startsWith('image/'));

                    const dom = imageFiles.length > 0 || /<img/i.test(htmlString) ? parseClipboardHtml(htmlString) : null;

                    if (imageFiles.length > 0 && !htmlHasContentBeyondImages(dom)) {
                        event.preventDefault();
                        insertImageFiles(imageFiles, $captureInsertionPoint());
                        return true;
                    }

                    if (!dom || !dom.querySelector('img')) return false;

                    event.preventDefault();
                    insertPastedHtml(dom, $captureInsertionPoint());
                    return true;
                },
                COMMAND_PRIORITY_HIGH,
            ),
            editor.registerCommand(
                DRAGOVER_COMMAND,
                (event) => {
                    if (!hasFiles(event.dataTransfer)) return false;
                    event.preventDefault();
                    return true;
                },
                COMMAND_PRIORITY_HIGH,
            ),
            editor.registerCommand(
                DROP_COMMAND,
                (event) => {
                    if (!hasFiles(event.dataTransfer)) return false;
                    const files = collectFiles(event.dataTransfer);
                    if (files.length === 0) return false;

                    event.preventDefault();
                    const insertionPoint = $insertionPointFromDrop(event, editor.getRootElement());
                    editor.focus();
                    insertImageFiles(files, insertionPoint);
                    return true;
                },
                COMMAND_PRIORITY_HIGH,
            ),
        );
    }, [editor, insertImageFiles, insertPastedHtml]);

    if (pendingUploads === 0) return null;

    return (
        <UploadStatus>
            <CircularProgress size={16} />
            <Typography variant="caption">{pendingUploads === 1 ? 'Uploading image…' : `Uploading ${pendingUploads} images…`}</Typography>
        </UploadStatus>
    );
}
